import saleutil from "@dotpe/rista-library/lib/sale/sale_util.js";
import { useSelector } from "react-redux";
import store from "../../redux-store";
import { getEnv } from "../../effects-bridge";
import { getLocalStorage } from "../../utils/localStorage";
import { LOCALSTORAGE_KEYS } from "../../utils/constants";
import { isBlockedCustomer } from "../../customerSearch";
/**
 * Uplifted pieces of `helper/commonHelpers` (apps/web ~1500 lines, apps/mobile ~900).
 *
 * This folder MIRRORS the app file it came from and is seeded with only the symbols that
 * were actually needed (plan §5.4) — `deepClone`, plus web's `createBranchPermittedUsersMap`
 * and its private closure `isPermittedBranchForUser` in `index.desktop.ts`. Together they are
 * the `commonHelpers` closure of `TableViewSlice` (web) and `DiscountSlice` (mobile).
 *
 * ⚠️ Most of what is left in the apps reads the app's redux `store` singleton — including
 * MOBILE's `isPermittedBranchForUser`, which is a genuinely different function from web's
 * (see `index.desktop.ts`). Those stay in the apps for now.
 *
 * ~~🔴 `store-bridge` is not the answer for a HELPER... nothing here should grow a store
 * dependency in the meantime.~~ **OBSOLETE — the store phase happened.** That rule was an
 * argument about TIMING: bridging a helper before the store moved would write the coupling
 * twice and unwind later. `store-bridge.ts` was deleted 2026-08-27 and the store itself now
 * lives in `packages/core/redux-store`, so there is no second coupling to avoid. A core
 * helper that needs state imports the store directly — `getBranchId` below does exactly
 * that, as do `saleHelpers`, `loyaltyHelper`, `discountHelper`, `OfflineHelper` and
 * `utils/apiService`. `effects-bridge` is the only seam left, and it covers effects, not
 * state.
 *
 * ✅ **What survives, and why it is worth keeping:** prefer passing state IN where a caller
 * exists to pass it — `createBranchPermittedUsersMap` takes `branchId` rather than reading
 * it — because a function with explicit inputs is testable with plain objects and cannot be
 * caught by the hazard below. That is now a preference for testability, not a ban.
 *
 * 🔴 **The live hard rule is different: never read the store at MODULE-EVAL time.** A direct
 * store import can still be `undefined` while the store module is itself evaluating, and
 * core has real import cycles (`redux-store → rootSaga → saga/pos/catalog → utils/apiService`).
 * Reads must sit inside a function body, the way `getBranchId` does — a top-level
 * `const branchId = store.getState()…` would break the bundle nondeterministically.
 * `pnpm audit:uplift` checks **H**, **I** and **J** enforce this; a module inside a cycle
 * that must answer before the store exists guards with `store?.getState?.()` (see
 * `kiosk/config/index.ts`).
 */

/** Byte-identical in both apps. */
export function deepClone(data: any = {}) {
  return JSON.parse(JSON.stringify(data));
}
export function isSupportLogin() {
  const isSupport = getLocalStorage(LOCALSTORAGE_KEYS.isSupportLogin, true);
  return isSupport;
}
/**
 * Array -> object keyed by a lower-cased field (default `mid`).
 *
 * ⚠️ **Cross-file uplift — the two apps imported this from DIFFERENT source files**: web from
 * `helper/commonHelpers`, mobile from `helper/catalogueHelpers`. The bodies are logically
 * identical (mobile's copy is plain `.js`, so it simply lacks web's type annotations — the kind
 * of difference the analyser is deliberately blind to).
 *
 * "One definition, always" outranks "mirror the source file" when the two sources disagree, so
 * it lives HERE, matching web's placement and the helper's generic nature. BOTH app files
 * re-export it, so neither app's importers changed.
 */
export function convertArrayToObject(inputArr: any, uniqueKey = "mid") {
  const lowerCase = (key: string) => key && key.toLocaleLowerCase();
  return inputArr.reduce(
    (acc: any, cur: any) => ({ ...acc, [lowerCase(cur[uniqueKey])]: cur }),
    {},
  );
}

/**
 * The branch this terminal is bound to. **Byte-identical in both apps** (diffed, not assumed) —
 * both read the same store path, so it is shared rather than duplicated.
 */
export const getBranchId = () => {
  return store.getState().businessStore?.businessData?.branchId;
};

/**
 * The loyalty providers configured for a given branch + channel, or `[]`.
 *
 * **Neither app's mobile file has this function** (grepped) — it came from web, and was only
 * ever `.desktop`-only "because no mobile consumer needs it yet, NOT because it is a web
 * concern" (hazard 32's distinction). It is pure — arguments in, array out, no store read — so
 * the move is exactly what that note anticipated.
 *
 * Two cross-platform callers pulled it over independently on 2026-09-04: the kiosk's contact
 * flow, and `packages/core/loyalty/selectors.ts` (a platform-neutral module for which this is
 * the one non-trivial dependency). Web importers are unchanged: `index.desktop.ts` re-exports
 * `./shared`.
 *
 * ⚠️ Delegates to `sale_util.js`'s `filterForProviders`, imported straight from the
 * `@dotpe/rista-library` package (never off a global — audit check K), and swallows any failure
 * into `[]`, logging to `console.error`. Preserved verbatim — callers treat an empty list as
 * "no provider configured".
 */
export function filterLoyaltyProviders(
  loyaltyProviders: any[],
  params: Record<string, string>,
): any[] {
  try {
    return saleutil.filterForProviders(loyaltyProviders ?? [], params) ?? [];
  } catch (e: any) {
    console.error("===>Error filtering loyalty providers", e);
    return [];
  }
}

// the mask lives in a leaf so the store-free my-orders View can import it; see ./phone
export { maskPhoneNumber } from "./phone";

/**
 * De-duplicate a list by one key, keeping the LAST occurrence of each.
 *
 * Uplifted 2026-09-11 for the item-label-settings printer picker, which merges four printer
 * sources (network, bluetooth, USB, local) that can each carry the same device.
 *
 * ⚠️ "Last wins" is a `Map`-insertion consequence, not a preference, and the printer merge
 * order depends on it. Preserved from `apps/web/src/helper/commonHelpers` verbatim; mobile had
 * no copy, so this is web's definition, now shared.
 */
export function getUniqueListByKey(
  arr: Record<string, any>[],
  key: string,
): Record<string, any>[] {
  return [...new Map(arr.map((item) => [item[key], item])).values()];
}

/**
 * 🔴 **A RE-EXPORT, not a definition.** `currency()` lived in this folder — in BOTH variants, as
 * two different functions — and now lives in `utils/currency`, the one module that owns the
 * currency concern. It is re-exported here because both apps' `helper/commonHelpers` re-export
 * this folder wholesale, so every existing `currency` importer in either app resolves through
 * this line and needed no edit.
 *
 * ⚠️ Nothing in THIS file calls it, so `export ... from` is sufficient — a re-export creates no
 * local binding (ledger rule 5). New core code should import `utils/currency` directly.
 */
export { currency, currencySymbolFor } from "../../utils/currency";

/**
 * The signed-in user's permission lists, and the two checks over them. The three readers are
 * byte-identical in both apps' `helper/commonHelpers`, so both re-export these.
 *
 * `featureRestrictions` / `moduleRestrictions` are WEB's bodies, which live in its
 * `helper/featureRestrictionHelper`. They sit HERE rather than in core's
 * `helpers/featureRestrictionHelper` because `saleHelpers.showCloseSaleCta` needs them and the
 * gate needs `saleHelpers`: a gate-side home would make the two folders import each other.
 *
 * ⚠️ Web's bodies return `0`, not `false`, when the list is empty (`[].length && …`). Only
 * truthiness is safe to read. Mobile keeps its own boolean versions for its non-adopted screens,
 * because a JSX `{featureRestrictions(x) && <View/>}` renders a bare `0` on React Native.
 */
export const allowedFeatures = () => {
  return (store.getState()?.businessStore?.userData?.allowedFeatures || []);
}

export const allowedModules = () => {
  return (store.getState()?.businessStore?.userData?.permittedModules || []);
}

export function getAssignedLicence(){
  return store.getState()?.businessStore?.userData?.assignedLicence
}

export const featureRestrictions = (role: string) => {
  return allowedFeatures()?.length && allowedFeatures().includes(role);
};

export const moduleRestrictions = (module: string) => {
  return allowedModules()?.length && allowedModules().includes(module);
};

/**
 * Whether this terminal belongs to the one account that recalculates every sale on the backend,
 * production included. Web's body, uplifted from the desktop half with `shouldCalculateSale`;
 * exported because that half's `preventCloseSaleEnable` asks it too.
 *
 * ⚠️ The account id comes through the effects bridge's `env` bag, not `process.env` — each app
 * reads it from its own build config (web `REACT_APP_SUBWAY_ACCOUNT_UUID`, mobile `config/index.js`).
 * The `|| ""` fallback is the app's own and is preserved, so an unset value returns a falsy result
 * rather than matching everything.
 */
export const isSubwayAccount = (): boolean => {
  const accountUUID = store.getState().businessStore?.businessData?.accountUUID;
  const SUBWAY_ACCOUNT_UUID = getEnv("subwayAccountUuid") || "";
  return SUBWAY_ACCOUNT_UUID && accountUUID === SUBWAY_ACCOUNT_UUID;
};

/**
 * Whether checkout and quick settle ask the backend to recalculate the sale first
 * (`sale/calculate`): the Subway account, or any build that is not beta / production. Web's
 * rule, now on both platforms; `isBetaOrProdEnv` crosses the bridge because the two apps derive it
 * differently (web `data/envConstants`, mobile `config/index.js`, which has no beta build).
 */
export const shouldCalculateSale = (): boolean => isSubwayAccount() || !getEnv("isBetaOrProdEnv");

/**
 * Is the customer on the customer form BLOCKED? True when the Rista record says so
 * (`customerInfo.isBlocked`) or its loyalty block does (`customerInfo.loyaltyInfo.isBlocked`);
 * the rule itself is `customerSearch`'s `isBlockedCustomer`, which the kiosk reads too.
 * DP-8122: both apps tag such a customer "Blocked Customer" on the customer step and hold its
 * CTA, and refuse "Edit Items" / "Add Items" on an order — see docs/flows/CUSTOMER_SEARCH.md.
 *
 * DP-8122 wrote this into BOTH apps' `helper/commonHelpers` with identical bodies, so it lives
 * here once. Both app files re-export it as `isCustomerBlocked`, the name DP-8122's call sites use
 * — all but mobile's `DeliveryDinein/customerInfo.tsx`, which imports it from here (it must not
 * load the app's `helper/commonHelpers`; the flow doc says why).
 *
 * 🔴 **A HOOK** — it calls `useSelector`, so call it unconditionally in a component body, never
 * from a callback, saga or plain helper. The `use` prefix is what lets `rules-of-hooks` check
 * this definition; the apps' `isCustomerBlocked` alias hides it from that rule at the call sites.
 *
 * ⚠️ Reads `customerInfo` ONLY. A block that only an EXTERNAL provider reports is not on the
 * record — `parseCustomerRecord` keeps an `isExternal` `loyaltyInfo` off it — but in
 * `externalLoyaltyData.loyaltyInfo`, which is deliberately not read here: it outlives the form
 * (`handleCancel` and the other `setCustomerDataSuccess({})` paths clear `customerInfo` and leave
 * it), so reading it would hold the next customer's CTA and lock unrelated orders.
 */
export function useIsCustomerBlocked(): boolean {
  return useSelector((state: any) => isBlockedCustomer(state.customerStore?.customerInfo));
}
export function selectTaxIdentificationLabel(state: any): { label: string; shortName: string } {
  const businessData = state?.businessStore?.businessData;

  if (businessData?.taxLabel?.label) {
    return {
      label: businessData.taxLabel.label,
      shortName: businessData.taxLabel.shortName || businessData.taxLabel.label,
    };
  }
  return {
    label: "GSTIN",
    shortName: "GSTIN",
  };
}
export const getTaxIdentificationLabel = () => selectTaxIdentificationLabel(store.getState());

/**
 * The code a close refused by `preventCloseSale` answers its caller with, so a caller that waits
 * on the callback is not left waiting: the payment engine sat in COMMITTING with Save and Confirm
 * dead. Its message is the guard's own toast (`posErrors`), so v1's callers, which toast
 * `getErrorMessage(code)`, show the words they already showed.
 */
export const CLOSE_NEGATIVE_BALANCE_CODE = "UI_ERR_CLOSE_NEGATIVE_BALANCE";
